import { useMemo, useState } from "react";
import { Search, Clock, Calendar, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import Reveal from "../components/Reveal";
import SectionLabel from "../components/SectionLabel";
import { useSite, resolveImg } from "../context/SiteContext";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function TravelJournal() {
  const { articles, categories } = useSite();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const featured = useMemo(() => articles.find((a) => a.featured) || articles[0], [articles]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((a) => a.slug !== featured?.slug)
      .filter((a) => (category === "All" ? true : a.category === category))
      .filter((a) =>
        !q
          ? true
          : a.title.toLowerCase().includes(q) ||
            a.excerpt.toLowerCase().includes(q) ||
            (a.seo || "").toLowerCase().includes(q)
      );
  }, [query, category, featured, articles]);

  if (!featured) return <main data-testid="journal-page" className="pt-32 pb-24" />;

  return (
    <main data-testid="journal-page" className="pb-24">
      {/* Featured hero */}
      <section className="pt-28 md:pt-32 max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionLabel>Travel Journal</SectionLabel>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-light text-balance max-w-3xl">
              Field notes from the <em className="not-italic font-medium">East</em>.
            </h1>
            <p className="text-muted-foreground max-w-md">
              Guides, road trips and slow stories — written from Shillong, for travellers heading our way.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <article className="group grid md:grid-cols-12 gap-0 rounded-3xl overflow-hidden border border-border bg-card" data-testid="journal-featured">
            <div className="md:col-span-7 relative h-72 md:h-[60vh] overflow-hidden">
              <img src={resolveImg(featured.cover)} alt={featured.title} className="h-full w-full object-cover transition-transform duration-[1500ms] group-hover:scale-105" />
              <div className="absolute top-5 left-5 glass rounded-full px-3 py-1 text-xs uppercase tracking-[0.25em]">Featured · {featured.category}</div>
            </div>
            <div className="md:col-span-5 p-8 md:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4">
                <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {formatDate(featured.date)}</span>
                <span>·</span>
                <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {featured.readTime} min read</span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-light leading-tight mb-4">{featured.title}</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">{featured.excerpt}</p>
              <div className="text-xs uppercase tracking-[0.25em] text-primary mb-6">By {featured.author}</div>
              <Button className="rounded-full self-start" data-testid="featured-read">
                Read article <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </article>
        </Reveal>
      </section>

      {/* Search + categories */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-16">
        <Reveal>
          <div className="flex flex-col gap-6">
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles, guides, places…"
                className="pl-11 h-12 rounded-full bg-card"
                data-testid="journal-search"
                aria-label="Search articles"
              />
            </div>

            <div className="flex flex-wrap gap-2 no-scrollbar" role="tablist" aria-label="Categories">
              {categories.map((c) => {
                const active = category === c;
                return (
                  <button
                    key={c}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setCategory(c)}
                    data-testid={`journal-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`px-4 h-9 rounded-full text-sm border transition-all ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-card text-foreground/80 border-border hover:border-primary/60"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>
      </section>

      {/* Articles grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-12">
        {filtered.length === 0 ? (
          <Reveal>
            <div className="rounded-3xl border border-dashed border-border p-14 text-center text-muted-foreground">
              No articles match your search yet. Try a different keyword or category.
            </div>
          </Reveal>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 0.06}>
                <article
                  data-testid={`journal-card-${a.slug}`}
                  className="group rounded-3xl overflow-hidden border border-border bg-card hover:shadow-2xl transition-shadow duration-500 h-full flex flex-col"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img src={resolveImg(a.cover)} alt={a.title} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-[1200ms]" loading="lazy" />
                    <div className="absolute top-4 left-4 glass rounded-full px-3 py-1 text-xs">{a.category}</div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                      <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {formatDate(a.date)}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {a.readTime} min</span>
                    </div>
                    <h3 className="font-display text-2xl leading-tight mb-2">{a.title}</h3>
                    <p className="text-sm text-muted-foreground mb-5 flex-1">{a.excerpt}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">By {a.author}</div>
                      <span className="text-primary text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">Read <ArrowRight className="h-3.5 w-3.5" /></span>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
